import { useState, useCallback, useEffect, useRef } from 'react';
import type { PaginatedResponse, ItemRenderer } from './paginated-select.types';

interface UsePaginatedSelectProps<T extends { id: string }> {
  mode?: 'single' | 'multiple';
  enableSearch?: boolean;
  fetchFn: (params: {
    page: number;
    pageSize: number;
    search?: string;
  }) => Promise<PaginatedResponse<T>>;
  pageSize?: number;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  disabled?: boolean;
  renderItem?: (item: T) => ItemRenderer;
  name?: string;
  control?: import('react-hook-form').Control;
  rules?: import('react-hook-form').RegisterOptions;
  error?: string;
}

export function usePaginatedSelect<T extends { id: string }>({
  mode = 'single',
  enableSearch = false,
  fetchFn,
  pageSize = 10,
  value: externalValue,
  onChange,
  disabled = false,
  renderItem: customRenderItem,
  error,
  name,
  control,
  rules,
}: UsePaginatedSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [internalValue, setInternalValue] = useState<string | string[]>(
    mode === 'multiple' ? [] : '',
  );

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const prevExternalValueRef = useRef(externalValue);
  const prevSearchRef = useRef(search);

  const value = externalValue !== undefined ? externalValue : internalValue;

  // Sincronizar internalValue cuando externalValue cambia
  useEffect(() => {
    // Cuando externalValue cambia de definido a undefined (deselección),
    // resetear internalValue
    if (
      mode === 'single' &&
      prevExternalValueRef.current !== undefined &&
      externalValue === undefined
    ) {
      setInternalValue('');
    }

    prevExternalValueRef.current = externalValue;
  }, [externalValue, mode]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (mode === 'multiple') {
        const currentValues = Array.isArray(value) ? value : [];
        const isSelected = currentValues.includes(newValue);
        const newValues = isSelected
          ? currentValues.filter((v) => v !== newValue)
          : [...currentValues, newValue];

        if (externalValue === undefined) {
          setInternalValue(newValues);
        }
        onChange?.(newValues);
      } else {
        const isAlreadySelected = value === newValue;
        const finalValue = isAlreadySelected ? '' : newValue;

        if (externalValue === undefined) {
          setInternalValue(finalValue);
        }
        onChange?.(finalValue);

        if (externalValue !== undefined) {
          if (!isAlreadySelected) {
            setOpen(false);
          }
        } else {
          setOpen(false);
        }
      }
    },
    [mode, value, externalValue, onChange],
  );

  const defaultRenderItem = useCallback((item: T): ItemRenderer => {
    return {
      value: item.id,
      label: (item as unknown as Record<string, unknown>)['name'] as string,
    };
  }, []);

  const renderItem = customRenderItem || defaultRenderItem;

  const fetchItems = useCallback(
    async (pageNum: number, searchTerm: string, append = false) => {
      try {
        if (pageNum === 1) {
          setIsLoading(true);
        } else {
          setIsFetchingMore(true);
        }

        const response = await fetchFn({
          page: pageNum,
          pageSize,
          search: searchTerm || undefined,
        });

        if (append) {
          setItems((prev) => [...prev, ...response.items]);
        } else {
          setItems(response.items);
        }

        setHasMore(response.hasNextPage);
        setPage(pageNum);
      } catch {
        setItems([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [fetchFn, pageSize],
  );

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      setSearch(searchTerm);
      setPage(1);
      setItems([]);
      setHasMore(true);
      fetchItems(1, searchTerm, false);
    },
    [fetchItems],
  );

  const loadMore = useCallback(() => {
    if (!isFetchingMore && hasMore && !isLoading) {
      fetchItems(page + 1, search, true);
    }
  }, [isFetchingMore, hasMore, isLoading, page, search, fetchItems]);

  useEffect(() => {
    if (open) {
      const shouldFetchPage1 = items.length === 0 || search !== prevSearchRef.current;

      if (shouldFetchPage1) {
        fetchItems(1, search, false);
      }

      prevSearchRef.current = search;
    }
  }, [open, search]);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    if (loadMoreRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            loadMore();
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [loadMore]);

  const isSelected = useCallback(
    (itemValue: string) => {
      if (mode === 'multiple') {
        return Array.isArray(value) && value.includes(itemValue);
      }
      return value === itemValue;
    },
    [mode, value],
  );

  const contextValue = {
    mode,
    enableSearch,
    open,
    setOpen,
    value,
    onValueChange: handleValueChange,
    search,
    setSearch: handleSearchChange,
    items: items as unknown as Record<string, unknown>[],
    setItems: ((newItems: unknown) => {
      setItems(newItems as T[]);
    }) as unknown as (items: Record<string, unknown>[]) => void,
    isLoading,
    setIsLoading,
    isFetchingMore,
    setIsFetchingMore,
    hasMore,
    setHasMore,
    page,
    setPage,
    pageSize,
    renderItem: renderItem as unknown as (item: Record<string, unknown>) => {
      value: string;
      label: string;
      disabled?: boolean;
    },
    error: error || '',
    disabled,
    name,
    control,
    rules,
    label: undefined as string | undefined,
    placeholder: undefined as string | undefined,
    helperText: undefined as string | undefined,
    isSelected,
    onLoadMore: () => {
      if (!isFetchingMore && hasMore && !isLoading) {
        fetchItems(page + 1, search, true);
      }
    },
  };

  return {
    contextValue,
    isSelected,
    loadMoreRef,
    loadMore,
  };
}
